import React, { useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { FormGroup, Input } from 'reactstrap'
import _ from 'lodash'

import { Show } from '~/components/commons'
import EditableRow from '~/components/EditableRow'
import { AditionalInfoContainer } from './styles'
import styled from 'styled-components'
import { PlusCircle as IconPlusCircle } from 'react-feather'

function VariablesDropdown({
  open,
  setOpen,
  fieldName,
  controller,
  disabled,
  onChange,
}) {
  const variablesList = [
    { label: 'Location name', value: '{{local_name}}' },
    { label: 'Location city', value: '{{local_city}}' },
    { label: 'Location area', value: '{{local_area}}' },
    { label: 'Location state', value: '{{local_state}}' },
    { label: 'Location code', value: '{{store_code}}' },
    { label: 'Location hours', value: '{{store_hours}}' },
  ]

  return (
    <>
      <Show condition={!disabled}>
        <VariableButton onClick={() => setOpen(!open)}>
          <IconPlusCircle
            size={10}
            style={{ color: '#fff', marginRight: '5px' }}
          />
          <FormattedMessage id="Variables" />
          <Show condition={open}>
            <VariableList>
              {variablesList.map((variable, i) => (
                <VariableItem
                  key={i}
                  onClick={() => {
                    if (onChange) {
                      const value = `${_.get(
                        controller?.values,
                        fieldName,
                        ''
                      )}${variable.value}`

                      const setValue = v =>
                        controller.setFieldValue(fieldName, v)

                      onChange(
                        {
                          target: {
                            value,
                          },
                        },
                        { setValue }
                      )
                    } else {
                      controller.setFieldValue(
                        fieldName,
                        `${_.get(controller?.values, fieldName, '')}${
                          variable.value
                        }`
                      )
                    }
                  }}
                >
                  <FormattedMessage id={variable.label} />
                </VariableItem>
              ))}
            </VariableList>
          </Show>
        </VariableButton>
      </Show>
    </>
  )
}

function Field({
  controller,
  name,
  label,
  render: Render = Input,
  onSwitchChange,
  editableFields,
  type,
  valueExtractor,
  children,
  onChange,
  uncontrolled,
  helperText,
  enableVariables,
  onToggleOff,
  ...props
}) {
  const [open, setOpen] = useState(false)

  const switchId = `${name}Switch`
  const inputRef = useRef(null)

  const value = useMemo(() => {
    if (uncontrolled) return
    if (_.has(props, 'value')) return props.value
    if (type !== 'select') return _.get(controller?.values, name)

    return props.options.find(
      option => option.value === _.get(controller?.values, name)
    )
  }, [controller.values, name, props, uncontrolled, type])

  const defaultExtractors = useMemo(
    () => ({
      text: event => event.target.value,
      select: option => option.value,
    }),
    []
  )

  const initialValue = useMemo(() => _.get(controller?.initialValues, name), [
    controller.initialValues,
    name,
  ])

  const definedValueExtractor = useMemo(() => {
    if (valueExtractor) return valueExtractor
    if (!type) return defaultExtractors.text
    if (defaultExtractors[type]) return defaultExtractors[type]
    return defaultExtractors.text
  }, [type, valueExtractor, defaultExtractors])

  const showInnerConteiner = children && !!editableFields?.[switchId]

  const uncontrolledProps = {
    ref: inputRef,
  }

  return (
    <>
      <EditableRow
        switchId={switchId}
        onChange={id => {
          if (editableFields?.[switchId]) {
            if (uncontrolled) {
              inputRef.current.value = initialValue
            }

            controller.setFieldValue(name, initialValue, false)
            if (onToggleOff) onToggleOff(name)
          }
          onSwitchChange(id)
        }}
        checkboxStyle={{
          alignSelf: 'start',
          marginTop: '27px',
          width: 'max-content',
        }}
        isChecked={!!editableFields?.[switchId]}
      >
        <FormGroup>
          <label htmlFor={name} className="d-flex justify-content-between">
            <FormattedMessage id={label} />:
            <Show condition={enableVariables}>
              <VariablesDropdown
                open={open}
                setOpen={setOpen}
                fieldName={name}
                controller={controller}
                disabled={!editableFields?.[switchId]}
                onChange={onChange}
              />
            </Show>
          </label>
          <Render
            id={name}
            name={name}
            disabled={!editableFields?.[switchId]}
            onChange={e => {
              const setValue = v => {
                controller.setFieldValue(name, v)
              }

              if (_.isFunction(onChange)) {
                onChange(e, { setValue })
              } else {
                setValue(definedValueExtractor(e))
              }
            }}
            editableFields={editableFields}
            switchId={switchId}
            controller={controller}
            {...(!uncontrolled && {
              value: value || initialValue,
            })}
            {...(uncontrolled && uncontrolledProps)}
            {...props}
          />
          <Show condition={!!showInnerConteiner}>
            <AditionalInfoContainer>{children}</AditionalInfoContainer>
          </Show>
          <Show condition={!!editableFields?.[switchId]}>
            <small style={{ marginTop: '5px' }}>{helperText}</small>
          </Show>
        </FormGroup>
      </EditableRow>
    </>
  )
}

const VariableButton = styled.div`
  background-color: #0091ff;
  height: 16px;
  font-size: 10px;
  color: #fff;
  border-radius: 8px 8px 0px 0px;
  line-height: 10px;
  font-family: 'Montserrat';
  margin: 2px 0 0 -2px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  margin-right: 10px;
  cursor: pointer;
  position: relative;
  overflow: visible;
`

const VariableList = styled.ul`
  position: absolute;
  width: 100%;
  left: 0;
  top: 18px;
  z-index: 100;
  padding: 0;
  box-shadow: 0px 4px 8px 2px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background-color: #fff;
`

const VariableItem = styled.li`
  z-index: 1000;
  list-style: none;
  padding: 2px 5px;
  color: #565656;
  user-select: none;

  &:hover {
    background: rgba(0, 145, 255, 0.7);
    color: #fff;
  }
`

export default Field
